/*  reducer-dashboard-widgets.ts
    --------------------------------------------------------------
    Keeps layout + size for every dashboard widget.
----------------------------------------------------------------- */
import {Action} from 'redux';
import RGL from 'react-grid-layout';
import {
  RDW_ADD_WIDGET,
  RDW_REMOVE_WIDGET,
  RDW_SET_LAYOUT,
  RDW_UPDATE_SIZE,
} from 'appredux/actionTypes';

/* TS-source-of-truth for widget IDs */
import type {WdgId} from 'screens/Dashboard/Overview/widgets';
import {reduxSnapItem} from 'screens/Dashboard/Overview/widgets/snapToZones';

/* RGL helper */
const {compact} = (RGL as any).utils;

/* ──────────────────────────────────────────────────────────── */
/* 1️⃣  Canonical desktop sizes (w = columns, h = rows × 32 px) */
export const widgetDefaults: Record<WdgId, {w: number; h: number}> = {
  /* classic seven */
  ordersStats: {w: 5, h: 4},
  ordersCount: {w: 5, h: 4},
  inputFunnelRands: {w: 4, h: 4},
  inputFunnelNumber: {w: 4, h: 4},
  topInputProducts: {w: 5, h: 6},
  storeViews: {w: 4, h: 5},
  productViews: {w: 5, h: 5},

  /* new widgets */
  fundingOverview: {w: 5, h: 8},
  recentApplicationChanges: {w: 4, h: 4},
  fundingPerProvince: {w: 5, h: 8},
  fundingPerCrop: {w: 5, h: 8},
  satelliteRisks: {w: 4, h: 4},
  walletMetrics: {w: 4, h: 5},
  programsOverview: {w: 4, h: 6},
  orderSummary: {w: 5, h: 6},
  quoteSummary: {w: 6, h: 6},
  favouriteProducts: {w: 4, h: 6},
  dataCapturingFeed: {w: 4, h: 6},
  farmersOverview: {w: 4, h: 6},
};

/* ──────────────────────────────────────────────────────────── */
/* 2️⃣  Shapes                                                */
export interface IGridItem extends RGL.Layout {
  i: WdgId;
}
export interface IRdwDashboardWidgets {
  layout: IGridItem[];
  lastPos: Record<WdgId, {x: number; y: number}>;
}

/* ──────────────────────────────────────────────────────────── */
/* 3️⃣  Initial seven-item layout (unchanged)                 */
const initialLayout: IGridItem[] = [
  {i: 'ordersStats', x: 0, y: 0, ...widgetDefaults.ordersStats},
  {i: 'inputFunnelRands', x: 5, y: 0, ...widgetDefaults.inputFunnelRands},
  {i: 'fundingOverview', x: 0, y: Infinity, ...widgetDefaults.fundingOverview},
  {i: 'ordersCount', x: 0, y: Infinity, ...widgetDefaults.ordersCount},
  {
    i: 'inputFunnelNumber',
    x: 5,
    y: Infinity,
    ...widgetDefaults.inputFunnelNumber,
  },
  {
    i: 'topInputProducts',
    x: 0,
    y: Infinity,
    ...widgetDefaults.topInputProducts,
  },
  {i: 'storeViews', x: 5, y: Infinity, ...widgetDefaults.storeViews},
  {i: 'productViews', x: 0, y: Infinity, ...widgetDefaults.productViews},
];

const initial: IRdwDashboardWidgets = {
  layout: initialLayout,
  lastPos: Object.fromEntries(
    initialLayout.map((l) => [l.i, {x: l.x, y: l.y}]),
  ) as Record<WdgId, {x: number; y: number}>,
};

/* ──────────────────────────────────────────────────────────── */
/* 4️⃣  Reducer                                               */
interface IReduxAction<T = unknown> extends Action<string> {
  payload?: T;
}

export default function reduxDashboardWidgets(
  state: IRdwDashboardWidgets = initial,
  action: IReduxAction,
): IRdwDashboardWidgets {
  switch (action.type) {
    /* ---------- add widget ---------- */
    case RDW_ADD_WIDGET: {
      const id = action.payload as WdgId;
      if (state.layout.some((l) => l.i === id)) return state;

      const {w, h} = widgetDefaults[id];
      const last = state.lastPos[id];

      const newItem: IGridItem = {
        i: id,
        w,
        h,
        x: last?.x ?? 0,
        y: last?.y ?? Infinity,
      };

      const packed = compact(
        [...state.layout, newItem],
        'vertical',
        9,
      ) as IGridItem[];
      return {layout: packed, lastPos: state.lastPos};
    }

    /* ---------- remove widget ---------- */
    case RDW_REMOVE_WIDGET: {
      const id = action.payload as WdgId;
      const item = state.layout.find((l) => l.i === id);
      if (!item) return state;

      return {
        layout: state.layout.filter((l) => l.i !== id),
        lastPos: {...state.lastPos, [id]: {x: item.x, y: item.y}},
      };
    }

    /* ---------- drag / resize ---------- */
    case RDW_SET_LAYOUT: {
      const incoming = JSON.parse(
        JSON.stringify(action.payload),
      ) as IGridItem[];
      const packed = compact(incoming, 'vertical', 9) as IGridItem[];
      const snapped = packed.map(reduxSnapItem) as IGridItem[];

      const lastPos = {...state.lastPos};
      snapped.forEach((l) => (lastPos[l.i] = {x: l.x, y: l.y}));

      return {layout: snapped, lastPos};
    }

    /* ---------- width toggle ---------- */
    case RDW_UPDATE_SIZE: {
      const {id, w} = action.payload as {id: WdgId; w: 4 | 5 | 9};

      const resized = state.layout.map((l) => (l.i === id ? {...l, w} : l));
      const packed = compact(resized, 'vertical', 9) as IGridItem[];
      const snapped = packed.map(reduxSnapItem) as IGridItem[];

      const lastPos = {...state.lastPos};
      snapped.forEach((l) => (lastPos[l.i] = {x: l.x, y: l.y}));

      return {layout: snapped, lastPos};
    }

    default:
      return state;
  }
}
